import { Link } from 'react-router-dom';

import { useRequete } from '../api/client';
import type { ApercuRadars, Fiche } from '../api/types';
import { useSession } from '../auth/session';
import { CadranRadar } from '../composants/CadranRadar';
import {
  Chargement,
  Chiffre,
  Fleche,
  Jauge,
  Message,
  dateLongue,
  joursRestants,
} from '../composants/elements';

/**
 * Page d'accueil — implémentation de « Radars Home.dc.html ».
 *
 * La maquette montrait deux radars écrits en dur. Ici les rangées sont
 * construites depuis le catalogue : deux radars aujourd'hui, trois demain, sans
 * toucher à cette page.
 */

/** Teintes des rangées, dans l'ordre du logo. Au-delà, on recycle. */
const TEINTES = ['#05BAEE', '#D6007F', '#004F99', '#AC67FF'];

function MarqueDecorative(): JSX.Element {
  return (
    <svg
      viewBox="0 0 400 560"
      aria-hidden="true"
      style={{
        position: 'absolute',
        top: -40,
        right: 'clamp(-160px, -6vw, 0px)',
        width: 'clamp(320px, 42vw, 560px)',
        height: 'auto',
        opacity: 0.1,
        pointerEvents: 'none',
      }}
    >
      <circle cx="200" cy="195" r="170" fill="#05BAEE" />
      <circle cx="200" cy="365" r="170" fill="#D6007F" />
      <path
        d="M52.8 280 A170 170 0 0 1 347.2 280 A170 170 0 0 1 52.8 280 Z"
        fill="#004F99"
      />
    </svg>
  );
}

export function Accueil(): JSX.Element {
  const session = useSession();
  const trimestre = session.moi?.campagneOuverte?.trimestre ?? null;

  const apercu = useRequete<ApercuRadars>('/api/radars');
  const fiche = useRequete<Fiche>(trimestre ? `/api/campagnes/${trimestre}/ma-fiche` : null);

  if (apercu.enCours && !apercu.donnees) return <Chargement quoi="Loading the radars" />;
  if (apercu.erreur) {
    return (
      <div className="contenu section">
        <Message variante="erreur">{apercu.erreur.message}</Message>
      </div>
    );
  }

  const donnees = apercu.donnees!;
  const reste = joursRestants(donnees.echeanceCourante);
  const progression = fiche.donnees?.progression;
  const echelle = session.moi?.echelle ?? [];

  return (
    <>
      {/* --- Hero ---------------------------------------------------------- */}
      <section className="contenu" style={{ position: 'relative', paddingBlock: 'clamp(48px, 8vw, 104px) clamp(40px, 6vw, 72px)' }}>
        <MarqueDecorative />

        <div className="apparait" style={{ position: 'relative', maxWidth: 780 }}>
          <p className="eyebrow" style={{ marginBottom: 20 }}>
            Elyadata Engineering ·{' '}
            {donnees.trimestreCourant
              ? `${donnees.trimestreCourant} campaign`
              : 'No campaign open'}
          </p>

          <h1 style={{ fontSize: 'clamp(36px, 6vw, 72px)', lineHeight: 1.04 }}>
            What we can actually
            <br />
            do, written down.
          </h1>

          <p className="plomb" style={{ maxWidth: 620, marginTop: 26, fontSize: 'clamp(17px, 1.6vw, 20px)' }}>
            The radars say what Elyadata recommends. This profile says who can actually work with
            it. A level from 0 to 5 per technology, written evidence from 3 up, one quarter at a
            time — enough to staff a role, build a training plan, and see that a technology sitting
            in <em>adopt</em> with nobody behind it is a delivery risk.
          </p>

          <div className="chiffres" style={{ marginTop: 'clamp(32px, 5vw, 52px)', paddingTop: 28, borderTop: '1px solid var(--border)' }}>
            <Chiffre valeur={donnees.total} libelle="Technologies to rate" />
            <Chiffre valeur={donnees.editionCourante ?? '—'} libelle="Radar edition" />
            <Chiffre valeur={donnees.contributeurs} libelle="Collaborators" />
            <Chiffre
              valeur={reste === null ? '—' : reste >= 0 ? `${reste} days` : 'overdue'}
              libelle="Until the deadline"
            />
          </div>
        </div>
      </section>

      {/* --- Ma fiche ------------------------------------------------------ */}
      {trimestre && progression ? (
        <section className="contenu" style={{ paddingBottom: 'clamp(40px, 6vw, 72px)' }}>
          <div
            style={{
              borderTop: '2px solid var(--ink)',
              paddingTop: 24,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 'clamp(20px, 4vw, 48px)',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ minWidth: 260 }}>
              <p className="eyebrow" style={{ marginBottom: 14 }}>
                My profile · {trimestre}
              </p>
              <h2 style={{ fontSize: 'clamp(24px, 2.6vw, 34px)' }}>
                {progression.repondu} / {progression.total} answered
              </h2>
              <div style={{ marginTop: 14, maxWidth: 320 }}>
                <Jauge progression={progression} />
              </div>
              <p className="discret" style={{ marginTop: 12 }}>
                {progression.aRevoir > 0
                  ? `${progression.aRevoir} line(s) carried over from last quarter, awaiting confirmation. `
                  : ''}
                {progression.incompletes > 0
                  ? `${progression.incompletes} line(s) without sufficient evidence. `
                  : ''}
                {fiche.donnees?.soumiseLe
                  ? `Submitted on ${dateLongue(fiche.donnees.soumiseLe)}, editable until the campaign closes.`
                  : `Deadline: ${dateLongue(donnees.echeanceCourante)}.`}
              </p>
            </div>

            <Link to="/ma-fiche" className="bouton bouton--rose">
              {progression.repondu === 0 ? 'Start my profile' : 'Resume my profile'}
              <Fleche />
            </Link>
          </div>
        </section>
      ) : null}

      {/* --- Les radars ---------------------------------------------------- */}
      <section className="contenu" style={{ paddingBottom: 'clamp(48px, 8vw, 88px)' }}>
        {donnees.radars.length === 0 ? (
          <Message variante="attention">
            No catalogue imported yet. An administrator needs to import a radar edition from the
            admin area.
          </Message>
        ) : null}

        {donnees.radars.map((r, index) => {
          const teinte = TEINTES[index % TEINTES.length]!;
          return (
            <Link
              key={r.radar}
              to={`/ma-fiche?radar=${encodeURIComponent(r.radar)}`}
              className="radar-rangee"
              style={{ '--teinte': teinte } as React.CSSProperties}
            >
              <div>
                <div className="radar-rangee__titre">
                  <span className="radar-rangee__index" style={{ color: teinte }}>
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <h2>{r.libelle}</h2>
                </div>

                <p className="plomb" style={{ maxWidth: 520, fontSize: 17 }}>
                  {r.groupes.length} group(s) to go through, from{' '}
                  {r.groupes[0]?.libelle.toLowerCase() ?? '—'} to{' '}
                  {r.groupes[r.groupes.length - 1]?.libelle.toLowerCase() ?? '—'}.
                </p>

                <div className="jetons">
                  {r.groupes.slice(0, 6).map((g) => (
                    <span key={g.cle} className="jeton">
                      {g.libelle} · {g.technologies}
                    </span>
                  ))}
                </div>

                <div className="chiffres" style={{ marginTop: 26, gap: 'clamp(20px, 4vw, 44px)' }}>
                  <Chiffre petit valeur={r.technologies} libelle="Technologies" />
                  <Chiffre petit valeur={r.nouvelles} libelle="New" />
                  <Chiffre petit valeur={r.deplacees} libelle="Moved" />
                  <Chiffre petit valeur={r.editionVue ?? '—'} libelle="Edition" />
                </div>

                <span className="lien-fleche" style={{ marginTop: 28 }}>
                  Rate {r.libelle.toLowerCase()}
                  <Fleche />
                </span>
              </div>

              <CadranRadar apercu={r} teinte={teinte} />
            </Link>
          );
        })}
      </section>

      {/* --- L'échelle ----------------------------------------------------- */}
      <section className="section section--grise">
        <div className="contenu">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: 'clamp(28px, 5vw, 72px)',
              alignItems: 'start',
            }}
          >
            <div>
              <p className="eyebrow" style={{ marginBottom: 18 }}>
                The scale
              </p>
              <h2>A level is evidence, not an impression.</h2>
              <p className="plomb" style={{ marginTop: 22, maxWidth: 460 }}>
                The level is about your skill, never about the technology — the recommendation
                stays with the radar. From 3 up, the line asks for written evidence of at least
                twenty characters: a project, a release, a date. It is the only safeguard a
                self-assessment has.
              </p>
              <p className="discret" style={{ marginTop: 18 }}>
                “Not applicable” is not a 0: a 0 answers about a technology in your scope, “not
                applicable” says it is not part of your job.
              </p>
            </div>

            <div className="pile">
              {echelle.map((e) => (
                <div
                  key={e.niveau}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '32px 1fr auto',
                    gap: 20,
                    padding: '18px 0',
                    borderTop: '1px solid var(--border)',
                    alignItems: 'start',
                  }}
                >
                  <span className="mono" style={{ fontSize: 15, color: 'var(--fg-3)', paddingTop: 2 }}>
                    {e.niveau}
                  </span>
                  <div>
                    <h4>{e.titre}</h4>
                    <p className="discret" style={{ marginTop: 4 }}>
                      {e.definition}
                    </p>
                  </div>
                  {e.preuveRequise ? (
                    <span className="badge badge--nouveau">Evidence</span>
                  ) : (
                    <span className="badge">—</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div
            className="grille-cartes"
            style={{ marginTop: 'clamp(36px, 5vw, 64px)', paddingTop: 32, borderTop: '1px solid var(--border)' }}
          >
            {[
              {
                anneau: 'adopt',
                titre: 'Adopt',
                texte:
                  'Proven on our engagements. No declared expert here is a signed delivery risk.',
              },
              {
                anneau: 'trial',
                titre: 'Trial',
                texte: 'Worth it on a project that can absorb the risk. We have used it.',
              },
              {
                anneau: 'assess',
                titre: 'Assess',
                texte: 'Under study in R&D. Understand it now, commit later, or not at all.',
              },
              {
                anneau: 'hold',
                titre: 'Hold',
                texte: 'Not for new work. Still widely practised, it is debt we did not know about.',
              },
            ].map((a) => (
              <div key={a.anneau} className="carte">
                <span className={`anneau anneau--${a.anneau}`} style={{ fontSize: 15, color: 'var(--ink)' }}>
                  <span className="anneau__point" aria-hidden="true" />
                  <strong style={{ fontFamily: 'var(--font-title)', fontWeight: 600 }}>{a.titre}</strong>
                </span>
                <p className="discret">{a.texte}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- Les éditions -------------------------------------------------- */}
      <section className="section">
        <div className="contenu">
          <div className="entete-page" style={{ paddingTop: 0 }}>
            <div>
              <p className="eyebrow" style={{ marginBottom: 16 }}>
                Quarters
              </p>
              <h2>Every campaign stays readable.</h2>
            </div>
            <p className="plomb" style={{ maxWidth: 380, fontSize: 15 }}>
              A closed campaign cannot be edited, not even by an administrator: history is only
              worth having if it is immutable.
            </p>
          </div>

          <div className="tableau-defilant">
            <table className="tableau">
              <thead>
                <tr>
                  <th>Quarter</th>
                  <th>Technologies</th>
                  <th>Opened</th>
                  <th>State</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {donnees.editions.map((e) => (
                  <tr key={e.trimestre}>
                    <td style={{ fontFamily: 'var(--font-title)', fontSize: 17, fontWeight: 600 }}>
                      {e.trimestre}
                    </td>
                    <td className="mono">{e.technologies}</td>
                    <td className="discret">{dateLongue(e.publieeLe)}</td>
                    <td>
                      <span
                        className={`badge${e.etat === 'ouverte' ? ' badge--nouveau' : e.etat === 'fermee' ? '' : ' badge--report'}`}
                      >
                        {e.etat === 'ouverte' ? 'Open' : e.etat === 'fermee' ? 'Closed' : 'Draft'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Link to={`/ma-fiche/${e.trimestre}`} className="discret">
                        View my profile →
                      </Link>
                    </td>
                  </tr>
                ))}
                {donnees.editions.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="discret">
                      No campaign created yet.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </>
  );
}
